'use client';

// ===================================================
// GAM Command Center — Block Handle + Block Menu
// Shows ⠿ (drag/menu) + ➕ (add) on block hover.
// Drag ⠿ to reorder (ProseMirror-native, nested-aware) · click ⠿ for the
// Block Menu (delete, duplicate, turn into…).
//
// Sprint 1 / B1 (2026-07-15): the hand-rolled HTML5 drag-to-reorder
// (mousemove positioning + dragover/drop + delete-then-insertContentAt via
// node.toJSON) was replaced by the official @tiptap/extension-drag-handle-react.
// The old approach was clamped to top-level blocks only (depth 1) — it could
// not drag a block nested inside a callout/toggle/column, and its position
// math accreted a string of live-fixed edge-case bugs. DragHandle does a real
// node-range move (nested-aware via edgeDetection) and StarterKit's Dropcursor
// renders the drop line, so all of that machinery is gone.
// ===================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import DragHandle from '@tiptap/extension-drag-handle-react';
import { defaultComputePositionConfig } from '@tiptap/extension-drag-handle';
import { offset } from '@floating-ui/dom';
import { EditorOverlay, EDITOR_OVERLAY_Z } from './editor-overlay';

interface BlockHandleProps {
  editor: Editor;
}

interface BlockMenuState {
  visible: boolean;
  x: number;
  y: number;
  nodePos: number;
  nodeType: string;
}

const TURN_INTO_OPTIONS = [
  { type: 'paragraph', label: 'טקסט', icon: '¶' },
  { type: 'heading', label: 'כותרת 1', icon: 'H1', attrs: { level: 1 } },
  { type: 'heading', label: 'כותרת 2', icon: 'H2', attrs: { level: 2 } },
  { type: 'heading', label: 'כותרת 3', icon: 'H3', attrs: { level: 3 } },
  { type: 'bulletList', label: 'רשימה', icon: '•' },
  { type: 'orderedList', label: 'ממוספרת', icon: '1.' },
  { type: 'taskList', label: 'משימות', icon: '☑' },
  { type: 'blockquote', label: 'ציטוט', icon: '"' },
];

// Nested-drag edge detection — lets a block be dropped into/out of container
// nodes (callout, toggle, and the upcoming columns/tabs) rather than only
// reordering top-level blocks.
//
// The threshold used to be -16, which silently disabled edge detection
// entirely: isNearEdge() tests `coords.x - rect.left < threshold`, and for a
// cursor inside the element that difference is always >= 0, so `0 < -16` never
// held. calculateEdgeDeduction() therefore always returned 0 and the parent was
// never preferred — you could never grab a whole callout/toggle/list by its
// edge. The official example uses a POSITIVE threshold (20).
//
// `edges` also has to be the RTL pair. The package ships 'right' explicitly as
// its RTL preset; the default ['left','top'] is the LTR one, and this editor is
// RTL by default (TextAlign defaultAlignment: 'right').
const NESTED_CONFIG = {
  edgeDetection: { edges: ['right', 'top'] as ('right' | 'top')[], threshold: 20 },
};

// Handle sits in the gutter with an 8px gap from the text.
// RTL: floating-ui resolves 'left'/'right' against the element's own direction,
// so the handle is pinned to the reading-start side by giving the floating
// element an explicit LTR context in CSS (see .drag-handle in editor.css) and
// asking for 'right-start' here — in an RTL document that is the right gutter,
// which is where the reader expects it.
const HANDLE_POSITION_CONFIG = {
  ...defaultComputePositionConfig,
  placement: 'right-start' as const,
  middleware: [offset(8)],
};

export function BlockHandle({ editor }: BlockHandleProps) {
  // Document position of the block the drag handle currently targets. Kept in
  // a ref (not state) because DragHandle fires onNodeChange on every hovered
  // block and the +/menu handlers only read it at click time.
  const currentPosRef = useRef<number>(-1);

  // Stable identity, on purpose — do NOT inline this back into <DragHandle>.
  //
  // DragHandle's own useEffect lists `onNodeChange` in its dependency array,
  // and that effect registers/unregisters a ProseMirror plugin. An inline
  // arrow here is a new function on every render of BlockHandle, so the
  // effect tore the plugin down and put it back on every single render —
  // and unregisterPlugin() swaps `state.plugins`, which makes ProseMirror
  // destroy and rebuild the view of EVERY plugin, not just this one.
  // The slash-command Suggestion plugin was one of the casualties: measured
  // 2026-09-06 on vdev, "/" fired its open event and the plugin view was
  // destroyed 87ms later, so the menu never became visible and the
  // suggestion state was reset — one autosave render was enough.
  // It only writes to a ref, so [] is the correct dependency list.
  const handleNodeChange = useCallback(({ pos }: { pos: number | null }) => {
    currentPosRef.current = typeof pos === 'number' ? pos : -1;
  }, []);
  const [menu, setMenu] = useState<BlockMenuState>({
    visible: false,
    x: 0,
    y: 0,
    nodePos: -1,
    nodeType: '',
  });
  const [showTurnInto, setShowTurnInto] = useState(false);

  // Close menu on outside click
  useEffect(() => {
    if (!menu.visible) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.gam-block-menu')) {
        setMenu((m) => ({ ...m, visible: false }));
        setShowTurnInto(false);
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [menu.visible]);

  // Add empty paragraph below the hovered block
  const handleAdd = useCallback(() => {
    const pos = currentPosRef.current;
    if (pos < 0) return;
    const node = editor.state.doc.nodeAt(pos);
    if (!node) return;

    const insertPos = pos + node.nodeSize;
    editor
      .chain()
      .focus()
      .insertContentAt(insertPos, { type: 'paragraph' })
      .setTextSelection(insertPos + 1)
      .run();
  }, [editor]);

  // Open block menu at the click point
  const handleMenuOpen = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const pos = currentPosRef.current;
      if (pos < 0) return;
      const node = editor.state.doc.nodeAt(pos);

      // Viewport coordinates, not editor-relative: the menu is portalled to
      // <body> (see EditorOverlay) so it is no longer positioned against the
      // editor box. It used to be absolute inside the editor's own scroll
      // container, which clipped it the moment the block sat near an edge —
      // the cut-off menu Gal photographed on 2026-09-06.
      setMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        nodePos: pos,
        nodeType: node?.type.name || '',
      });
      setShowTurnInto(false);
    },
    [editor]
  );

  // Delete block
  const handleDelete = useCallback(() => {
    if (menu.nodePos < 0) return;
    const node = editor.state.doc.nodeAt(menu.nodePos);
    if (!node) return;

    editor
      .chain()
      .focus()
      .deleteRange({ from: menu.nodePos, to: menu.nodePos + node.nodeSize })
      .run();
    setMenu((m) => ({ ...m, visible: false }));
  }, [editor, menu.nodePos]);

  // Duplicate block
  const handleDuplicate = useCallback(() => {
    if (menu.nodePos < 0) return;
    const node = editor.state.doc.nodeAt(menu.nodePos);
    if (!node) return;

    const insertPos = menu.nodePos + node.nodeSize;
    editor
      .chain()
      .focus()
      .insertContentAt(insertPos, node.toJSON())
      .run();
    setMenu((m) => ({ ...m, visible: false }));
  }, [editor, menu.nodePos]);

  // Turn into another block type
  const handleTurnInto = useCallback(
    (type: string, attrs?: Record<string, unknown>) => {
      if (menu.nodePos < 0) return;

      editor.chain().focus().setTextSelection(menu.nodePos + 1).run();

      switch (type) {
        case 'paragraph':
          editor.chain().focus().setParagraph().run();
          break;
        case 'heading':
          editor.chain().focus().setHeading(attrs as { level: 1 | 2 | 3 }).run();
          break;
        case 'bulletList':
          editor.chain().focus().toggleBulletList().run();
          break;
        case 'orderedList':
          editor.chain().focus().toggleOrderedList().run();
          break;
        case 'taskList':
          editor.chain().focus().toggleTaskList().run();
          break;
        case 'blockquote':
          editor.chain().focus().setBlockquote().run();
          break;
      }

      setMenu((m) => ({ ...m, visible: false }));
      setShowTurnInto(false);
    },
    [editor, menu.nodePos]
  );

  return (
    <>
      {/* Drag handle (official, nested-aware) — child is the visible grip */}
      <DragHandle
        editor={editor}
        nested={NESTED_CONFIG}
        computePositionConfig={HANDLE_POSITION_CONFIG}
        onNodeChange={handleNodeChange}
      >
        <div className="gam-block-handle" contentEditable={false}>
          <button
            className="gam-block-handle__btn gam-block-handle__add"
            onClick={handleAdd}
            title="הוסף בלוק"
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <button
            className="gam-block-handle__btn gam-block-handle__drag"
            onClick={handleMenuOpen}
            title="גרור לשינוי סדר · לחיצה לתפריט"
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="5" cy="3" r="1.2" fill="currentColor" />
              <circle cx="9" cy="3" r="1.2" fill="currentColor" />
              <circle cx="5" cy="7" r="1.2" fill="currentColor" />
              <circle cx="9" cy="7" r="1.2" fill="currentColor" />
              <circle cx="5" cy="11" r="1.2" fill="currentColor" />
              <circle cx="9" cy="11" r="1.2" fill="currentColor" />
            </svg>
          </button>
        </div>
      </DragHandle>

      {/* Block Menu — portalled, so nothing in the editor can clip it. */}
      {menu.visible && (
        <EditorOverlay>
        <div
          className="gam-block-menu"
          style={{ top: `${menu.y}px`, left: `${menu.x}px`, zIndex: EDITOR_OVERLAY_Z }}
        >
          <button className="gam-block-menu__item" onClick={handleDelete}>
            <span className="gam-block-menu__icon">🗑</span>
            <span>מחק</span>
          </button>
          <button className="gam-block-menu__item" onClick={handleDuplicate}>
            <span className="gam-block-menu__icon">📋</span>
            <span>שכפל</span>
          </button>
          <div className="gam-block-menu__separator" />
          {editor.view.dom.classList.contains('gam-free-position-active') && menu.nodePos >= 0 && editor.state.doc.resolve(menu.nodePos).depth === 0 && (
            <button className="gam-block-menu__item" onClick={() => {
              const node = editor.state.doc.nodeAt(menu.nodePos);
              const el = editor.view.nodeDOM(menu.nodePos) as HTMLElement | null;
              if (!node || !el || !editor.isEditable) return;
              const root = editor.view.dom.getBoundingClientRect(), rect = el.getBoundingClientRect();
              const scale = root.width / editor.view.dom.offsetWidth || 1;
              editor.view.dispatch(editor.state.tr.setNodeMarkup(menu.nodePos, undefined, { ...node.attrs,
                freePosition: { x: Math.max(0, (rect.left - root.left) / scale), y: Math.max(26, (rect.top - root.top) / scale), width: Math.max(80, rect.width / scale), flowHeight: rect.height / scale + 8 },
              }));
              setMenu(m => ({ ...m, visible: false }));
            }}>מיקום חופשי לבלוק</button>
          )}
          <button
            className="gam-block-menu__item"
            onClick={() => setShowTurnInto(!showTurnInto)}
          >
            <span className="gam-block-menu__icon">🔄</span>
            <span>הפוך ל...</span>
            <span className="gam-block-menu__arrow">◂</span>
          </button>
          {showTurnInto && (
            <div className="gam-block-menu__sub">
              {TURN_INTO_OPTIONS.map((opt) => (
                <button
                  key={`${opt.type}-${opt.attrs?.level || ''}`}
                  className="gam-block-menu__item"
                  onClick={() => handleTurnInto(opt.type, opt.attrs)}
                >
                  <span className="gam-block-menu__icon">{opt.icon}</span>
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        </EditorOverlay>
      )}
    </>
  );
}
